import dayjs from 'dayjs'
import { ClipboardList, ExternalLink } from 'lucide-react'
import { Bar, Pie } from 'react-chartjs-2'
import GaugeComponent from 'react-gauge-component'

import { OrderStatusBadgeComponent } from '@/modules/shared/components/order-status-badge.component'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/modules/shared/components/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/modules/shared/components/ui/table'
import { cn } from '@/modules/shared/lib/utils'
import { formatDateBy } from '@/modules/shared/utils/dateFormatter'
import {
	numberFormatter,
	priceFormatter,
} from '@/modules/shared/utils/formatter'

import type { useCustomerProfile } from '../hooks/use-customer-profile.hook'
import { ButtonStartOrderComponent } from './button-start-order.component'
import { useEffect, useState } from 'react'

type Props = ReturnType<typeof useCustomerProfile>

export function CustomerChartComponent({
	dataProfile,
	dataOfFamilyProfile,
	handleNavigationOrderToVisualization,
	handleStartOrder,
	dataOfReportProfile,
	productData,
}: Props) {

	const [previsao, setPrevisao] = useState<string | null>(null)



	useEffect(() => {
		const ultimaCompra = dataProfile?.perfil?.ultimaCompra;
		const frequenciaCompra = dataProfile?.perfil?.frequenciaCompra;

		if (ultimaCompra && frequenciaCompra) {
			const dataPrevisao = dayjs(ultimaCompra).add(frequenciaCompra, 'days');
			if (dataPrevisao.isValid()) {
				setPrevisao(dataPrevisao.format('DD/MM/YYYY'));
			} else {
				setPrevisao('Data inválida');
			}
		} else {
			setPrevisao(null);
		}
	}, [dataProfile?.perfil?.ultimaCompra, dataProfile?.perfil?.frequenciaCompra])
	return (
		<div className="space-y-4">
			<h2 className="font-bold">Perfil de vendas</h2>
			<div className="space-y-2">
				<div className="grid grid-cols-2 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 text-sm">
					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md">Última venda</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{dataProfile?.perfil?.ultimaCompra
								? formatDateBy(dataProfile?.perfil?.ultimaCompra, 'DD/MM/YY')
								: 'Não possui venda faturada'}
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md">Valor última venda</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{priceFormatter.format(dataProfile?.perfil?.valorUltimaCompra)}
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md">Valor médio</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{priceFormatter.format(dataProfile?.perfil?.valorMedioVenda)}
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md flex justify-center items-center gap-2">
								Frequência <p className="font-normal text-xs">(Em dias)</p>
							</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{`${dataProfile?.perfil?.frequenciaCompra}`}
							{
								previsao && (
									<p className="text-xs">
										{
											`Previsão ${previsao}`
										}
									</p>
								)
							}
						</CardContent>
					</Card>
					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md">Qtd. de vendas</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{dataProfile?.perfil?.qtdVendas}
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="p-2 -mb-3 text-center">
							<CardTitle className="text-md">Primeira venda</CardTitle>
						</CardHeader>

						<CardContent className="p-2 text-center">
							{dataProfile?.perfil?.primeiraCompra
								? formatDateBy(dataProfile?.perfil?.primeiraCompra, 'DD/MM/YY')
								: 'Não possui venda faturada'}
						</CardContent>
					</Card>
				</div>
				<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
					<Card>
						<CardHeader>
							<CardTitle className="text-md">Últimos pedidos</CardTitle>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Id</TableHead>
										<TableHead>Data</TableHead>
										<TableHead>Valor</TableHead>
										<TableHead>Status</TableHead>
										<TableHead />
									</TableRow>
								</TableHeader>

								<TableBody>
									{dataProfile?.pedidos?.map((item, index) => (
										<TableRow
											key={item.idPedido}
											className={cn(
												'text-xs md:text-sm',
												index % 2 === 0 && 'bg-gray-100',
											)}
										>
											<TableCell>{item.idPedido}</TableCell>
											<TableCell>{formatDateBy(item.data, 'DD/MM/YY')}</TableCell>
											<TableCell>{numberFormatter.format(item.valorTotal)}</TableCell>
											<TableCell>
												<div className="flex items-center flex-row">
													<OrderStatusBadgeComponent
														status={item.status}
														sigla={item.statusSigla}
													/>
												</div>
											</TableCell>
											<TableCell
												className="cursor-pointer"
												onClick={() =>
													handleNavigationOrderToVisualization(item.idPedidoCel)
												}
											>
												<ExternalLink size={15} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
								{dataProfile?.pedidos.length === 0 && (
									<TableBody>
										<TableRow>
											<TableCell colSpan={5} className="text-center">
												Não possui pedidos
											</TableCell>
										</TableRow>
									</TableBody>
								)}
							</Table>
						</CardContent>
					</Card>

					{dataProfile?.score?.sequencia > 0 ? (
						<div>
							<Card>
								<CardHeader>
									<CardTitle className="text-md flex gap-2 items-center">
										Score de vendas em R${' '}
										<p className="font-normal text-sm">(6 Últimos meses)</p>
									</CardTitle>
								</CardHeader>

								<CardContent className="p-0">
									<div className="justify-center flex items-center  text-xs md:h-[320px] w-full text-gray-800">
										<GaugeComponent
											style={{
												width: '100%',
											}}
											type="semicircle"
											arc={{
												subArcs: [
													{
														limit: dataProfile?.score?.sequenciaMax * 0.1,
													},
													{
														limit: dataProfile?.score?.sequenciaMax * 0.6,
													},
													{
														limit: dataProfile?.score?.sequenciaMax,
													},
												],
												gradient: true,
												width: 0.3,
												colorArray: ['#EA4228', '#F5CD19', '#5BE12C'],
											}}
											pointer={{
												color: '#232323',
												length: 0.8,
												width: 15,
												// elastic: true,
											}}
											labels={{
												valueLabel: {
													style: {
														fill: '#232323',
														borderColor: '#fff',
														textShadow: '1px 1px 1px #fff',
														fontWeight: 'bold',
													},
												},
												tickLabels: {
													type: 'outer',
												},
											}}
											minValue={0}
											maxValue={dataProfile?.score?.sequenciaMax}
											value={dataProfile?.score?.sequencia}
										/>
									</div>
								</CardContent>
							</Card>
						</div>
					) : (
						<div className="flex-1 h-full">
							<Card className="h-full">
								<CardHeader>
									<CardTitle className="text-md">
										Score de vendas em R$
									</CardTitle>
								</CardHeader>

								<CardContent className="p-2 items-center text-center flex-col flex justify-center flex-1">
									<ClipboardList size={42} className="text-gray-300 mb-4" />
									<p className="text-sm">
										Nenhuma informação
									</p>
								</CardContent>
							</Card>
						</div>
					)}

					<div className="flex flex-1 flex-col w-full">
						{dataOfReportProfile?.labels?.length > 0 ? (
							<Card className="flex flex-1 flex-col">
								<CardHeader>
									<CardTitle className="text-md">Top 10 produtos</CardTitle>
								</CardHeader>

								<CardContent className="p-0">
									<div className="justify-center flex items-center flex-1 h-full w-full">
										<Bar
											data={productData}
											options={{
												plugins: {
													datalabels: {
														color: '#fff',
														formatter: (value: number) => {
															return `${value.toFixed(2)}%`
														},
													},
												},
												indexAxis: 'y' as const,
												responsive: true,
												normalized: true,
												scales: {
													x: {
														title: {
															font: {
																size: 8,
															},
														},
														ticks: {
															font: {
																size: 8,
															},
														},
													},
													y: {
														title: {
															font: {
																size: 10,
															},
														},

														display: true,
														position: 'left',
														ticks: {
															font: {
																size: 8,
															},
														},
													},
												},
											}}
										/>
									</div>
								</CardContent>
							</Card>
						) : (
							<Card>
								<CardHeader>
									<CardTitle className="text-md">Top 10 produtos</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="justify-center flex flex-col items-center h-[264px] md:h-[320px]">
										<ClipboardList size={42} className="text-gray-300 mb-4" />
										<p className="text-sm">
											{' '}
											Nenhuma informação
										</p>
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					<div>
						{dataOfReportProfile?.labels?.length > 0 ? (
							<Card>
								<CardHeader>
									<CardTitle className="text-md">Vendas por familia</CardTitle>
								</CardHeader>

								<CardContent>
									<div className="justify-center flex items-center h-[264px] md:h-[320px]">
										<Pie
											data={dataOfFamilyProfile}
											options={{
												plugins: {
													datalabels: {
														display: false,
													},
												},
											}}
										/>
									</div>
								</CardContent>
							</Card>
						) : (
							<Card>
								<CardHeader>
									<CardTitle className="text-md">Vendas por familia</CardTitle>
								</CardHeader>

								<CardContent>
									<div className="justify-center flex flex-col items-center h-[264px] md:h-[320px]">
										<ClipboardList size={42} className="text-gray-300 mb-4" />
										<p className="text-sm">
											{' '}
											Nenhuma informação
										</p>
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				</div>
				<ButtonStartOrderComponent onStartOrder={handleStartOrder} />
			</div>
			<h2 className="font-bold">Perfil de pagamento</h2>
			<div className="space-y-2">
				<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
					<Card>
						<CardHeader className="flex w-full flex-col lg:flex-row">
							<CardTitle className="text-md w-full">Contas a receber</CardTitle>
							<div className="grid grid-cols-2 gap-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2 text-sm w-full">
								<Card>
									<CardHeader className="p-2 -mb-3 text-center">
										<CardTitle className="text-md">Total vencidas</CardTitle>
									</CardHeader>

									<CardContent className="p-2 text-center font-bold text-red-500">
										{priceFormatter.format(Number.isNaN(dataProfile?.perfil?.vencido) ? 0 : dataProfile?.perfil?.vencido)}
									</CardContent>
								</Card>
								<Card>
									<CardHeader className="p-2 -mb-3 text-center">
										<CardTitle className="text-md">Total abertas</CardTitle>
									</CardHeader>

									<CardContent className="p-2 text-center">
										{priceFormatter.format(Number.isNaN(dataProfile?.perfil?.aberto) ? 0 : dataProfile?.perfil?.aberto)}
									</CardContent>
								</Card>
							</div>
						</CardHeader>
						<CardContent>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Nº Documento</TableHead>
										<TableHead>Valor</TableHead>
										<TableHead>Data</TableHead>
										<TableHead>Vencimento</TableHead>
										<TableHead>Dias de Atraso</TableHead>
									</TableRow>
								</TableHeader>

								<TableBody>
									{dataProfile?.contasReceber?.map((item, index) => (
										<TableRow
											key={item.numDoc}
											className={cn(
												'text-xs md:text-sm',
												index % 2 === 0 && 'bg-gray-100',
												item.diasAtraso > 0 && 'text-red-500',
											)}
										>
											<TableCell>{item.nrDoc}</TableCell>
											<TableCell>
												{priceFormatter.format(Number.isNaN(item.valor) ? 0 : item.valor)}
											</TableCell>
											<TableCell>
												{formatDateBy(item.data, 'DD/MM/YY')}
											</TableCell>
											<TableCell>
												{formatDateBy(item.dataVencimento, 'DD/MM/YY')}
											</TableCell>
											<TableCell>{item.diasAtraso}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
					<div>
						{dataOfReportProfile?.labels?.length > 0 ? (
							<Card>
								<CardHeader>
									<CardTitle className="text-md">
										Gráfico de pontualidade
									</CardTitle>
								</CardHeader>

								<CardContent>
									<div className="justify-center flex items-center h-[264px] md:h-[320px]">
										<Pie
											data={dataOfReportProfile}
											options={{
												plugins: {
													datalabels: {
														display: true,
														color: '#fff',
														formatter: (value: number) => {
															return `${value.toFixed(2)}%`
														},
													},
												},
											}}
										/>
									</div>
								</CardContent>
							</Card>
						) : (
							<Card>
								<CardHeader>
									<CardTitle className="text-md">
										Gráfico de pontualidade
									</CardTitle>
								</CardHeader>

								<CardContent>
									<div className="justify-center flex items-center flex-col h-[264px] md:h-[320px]">
										<ClipboardList size={42} className="text-gray-300 mb-4" />
										<p className="text-sm">
											Nenhuma informação
										</p>
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				</div>
				<ButtonStartOrderComponent onStartOrder={handleStartOrder} />
			</div>
		</div>
	)
}
